/**
 * The jobs staff can start for a team (GET /api/internal/teams/{id}/jobs,
 * services/team_jobs.py), with what the cluster says about the team's
 * runs of each. Staff only: the staff Jobs page and the Data refinery's
 * Generate tasks read it. `useTeamJobLog` reads the newest lines of one
 * of those runs for the Jobs page's log sheet.
 */

import { useQueryClient } from '@tanstack/react-query'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import {
  getListTeamJobsApiInternalTeamsTeamIdJobsGetQueryKey,
  useListTeamJobsApiInternalTeamsTeamIdJobsGet,
  useReadTeamJobLogApiInternalTeamsTeamIdJobsJobSlugRunsJobNameLogGet,
} from '@/api/generated/internal/internal'
import type { TeamJobItem, TeamJobLogResponse } from '@/api/generated/model'

// A run takes minutes to hours, so the page keeps up with an active one
// and only checks in on a quiet team.
const ACTIVE_REFETCH_MS = 10_000
const IDLE_REFETCH_MS = 60_000

export const ACTIVE_REASON = 'A run is active. Wait for it to finish.'

export interface TeamJobs {
  jobs: TeamJobItem[]
  isLoading: boolean
  /** Null while the list is fine. */
  error: string | null
  refresh: () => void
}

/** Why the job cannot start now, or null when it can. `active` says a run of the team's is going. */
export function jobBlocker(job: TeamJobItem, active: string = ACTIVE_REASON): string | null {
  return job.unavailable_reason ?? (job.active_job_name === null ? null : active)
}

export function useTeamJobs(teamId: string): TeamJobs {
  const queryClient = useQueryClient()
  const query = useListTeamJobsApiInternalTeamsTeamIdJobsGet(teamId, {
    query: {
      retry: false,
      refetchInterval: (current) => {
        const body = current.state.data
        const listed = body?.status === 200 ? body.data.jobs : []
        return listed.some((job) => job.active_job_name !== null)
          ? ACTIVE_REFETCH_MS
          : IDLE_REFETCH_MS
      },
    },
  })
  return {
    jobs: query.data?.status === 200 ? query.data.data.jobs : [],
    isLoading: query.isLoading,
    error: query.isError ? (getApiErrorDetail(query.error) ?? 'Failed to load jobs.') : null,
    refresh: () => {
      void queryClient.invalidateQueries({
        queryKey: getListTeamJobsApiInternalTeamsTeamIdJobsGetQueryKey(teamId),
      })
    },
  }
}

export interface TeamJobLog {
  log: TeamJobLogResponse | undefined
  isLoading: boolean
  /** True while any read is out, the first or a refresh. */
  isFetching: boolean
  /** Null while the read is fine. */
  error: string | null
  refresh: () => void
}

/** The latest read of a run's log, as `nextLogReadMs` weighs it. */
export interface LogRead {
  succeeded: boolean
  /** The HTTP status of a read that failed; undefined for one that did not. */
  errorStatus: number | undefined
  /** The last log a read returned, kept through a later failure. */
  log: TeamJobLogResponse | undefined
}

/**
 * When to read a run's log again: on the list's cadence until the latest
 * read succeeded and found the run ended, with its last lines. A read that
 * failed is read again too, so a sheet opened during a blip recovers by
 * itself rather than waiting on Refresh. A 404 is not: the run is gone
 * (its Job outlived its TTL), and no later read finds it.
 */
export function nextLogReadMs(read: LogRead): number | false {
  if (read.errorStatus === 404) return false
  return read.succeeded && read.log !== undefined && !read.log.is_active ? false : ACTIVE_REFETCH_MS
}

/**
 * The newest lines of one run's log, read again by `nextLogReadMs` while
 * the sheet is open. A finished run's log is read each time it is opened,
 * never from the cache, since it may have been opened while the run was
 * active. Null `jobName` reads nothing.
 */
export function useTeamJobLog(teamId: string, jobSlug: string, jobName: string | null): TeamJobLog {
  const query = useReadTeamJobLogApiInternalTeamsTeamIdJobsJobSlugRunsJobNameLogGet(
    teamId,
    jobSlug,
    jobName ?? '',
    {
      query: {
        enabled: jobName !== null,
        retry: false,
        staleTime: 0,
        refetchInterval: (current) => {
          const { data: body, error, status } = current.state
          return nextLogReadMs({
            succeeded: status === 'success',
            errorStatus: status === 'error' ? getApiErrorStatus(error) : undefined,
            log: body?.status === 200 ? body.data : undefined,
          })
        },
      },
    },
  )
  return {
    log: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? (getApiErrorDetail(query.error) ?? 'Failed to load the log.') : null,
    refresh: () => {
      void query.refetch()
    },
  }
}
